import { useEffect, useState, useRef } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Button } from '@bancobarigui/barihaus';
import { PlusOutlined } from '@ant-design/icons';
import { Group, Question } from '../types';
import { GroupItem } from '../GroupItem';
import { EmptyState } from '../EmptyState';

interface WorkspaceContentProps {
  groups: Group[];
  draggedType: string | null;
  isDragOverGroups: boolean;
  onAddGroup: (insertIndex?: number) => void;
  onEditGroup: (groupId: string, name: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onDuplicateGroup: (groupId: string) => void;
  onAddSection: (groupId: string, insertIndex?: number) => void;
  onEditSection: (groupId: string, sectionId: string, name: string) => void;
  onRemoveSection: (groupId: string, sectionId: string) => void;
  onDuplicateSection: (groupId: string, sectionId: string) => void;
  onAddQuestion: (groupId: string, sectionId: string, insertIndex?: number) => void;
  onEditQuestion: (groupId: string, sectionId: string, questionId: string, updates: Partial<Question>) => void;
  onRemoveQuestion: (groupId: string, sectionId: string, questionId: string) => void;
  onDuplicateQuestion: (groupId: string, sectionId: string, questionId: string) => void;
}

export function WorkspaceContent({
  groups,
  draggedType,
  isDragOverGroups: _isDragOverGroups,
  onAddGroup,
  onEditGroup,
  onRemoveGroup,
  onDuplicateGroup,
  onAddSection,
  onEditSection,
  onRemoveSection,
  onDuplicateSection,
  onAddQuestion,
  onEditQuestion,
  onRemoveQuestion,
  onDuplicateQuestion,
}: WorkspaceContentProps) {
  const [enabled, setEnabled] = useState(false);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  const handleAutoScroll = (mouseY: number) => {
    const scrollThreshold = 100;
    const scrollSpeed = 10;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (mouseY < scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, -scrollSpeed);
      }, 16);
    } else if (mouseY > window.innerHeight - scrollThreshold) {
      scrollIntervalRef.current = window.setInterval(() => {
        window.scrollBy(0, scrollSpeed);
      }, 16);
    }
  };

  const handleGroupDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedType !== 'new-group') {
      setDropIndex(null);
      return;
    }

    handleAutoScroll(e.clientY);

    if (groups.length === 0) {
      setDropIndex(0);
      return;
    }

    if (!containerRef.current) return;

    const mouseY = e.clientY;
    let targetIndex = groups.length;

    for (let i = 0; i < groups.length; i++) {
      const groupEl = groupRefs.current.get(i);
      if (groupEl) {
        const rect = groupEl.getBoundingClientRect();
        const middleY = rect.top + rect.height / 2;

        if (mouseY < middleY) {
          targetIndex = i;
          break;
        }
      }
    }

    setDropIndex(targetIndex);
  };

  const handleGroupDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!containerRef.current?.contains(relatedTarget)) {
      setDropIndex(null);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
  };

  const handleGroupDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const controlType = e.dataTransfer.getData('text/plain');

    if (controlType === 'new-group' && dropIndex !== null) {
      onAddGroup(dropIndex);
    }

    setDropIndex(null);

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <>
      <Droppable droppableId="groups" type="group">
        {(provided, snapshot) => (
          <div
            ref={(el) => {
              provided.innerRef(el);
              if (el && containerRef.current !== el) {
                (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }
            }}
            {...provided.droppableProps}
            onDragOver={handleGroupDragOver}
            onDragLeave={handleGroupDragLeave}
            onDrop={handleGroupDrop}
            style={{
              minHeight: groups.length === 0 ? '200px' : 'auto',
              backgroundColor: snapshot.isDraggingOver ? 'rgba(0, 0, 255, 0.05)' : 'transparent',
              transition: 'background-color 0.2s'
            }}
          >
            {groups.length === 0 && draggedType !== 'new-group' && <EmptyState onAddGroup={() => onAddGroup()} />}
            {groups.length === 0 && dropIndex === 0 && (
              <div
                className={`mt-4 mb-4 p-6 border-2 border-dashed rounded-lg transition-all ${
                  'border-blue-400 bg-blue-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-gray-500">
                  Solte aqui para adicionar primeiro grupo
                </p>
              </div>
            )}
            {dropIndex === 0 && groups.length > 0 && (
              <div
                className={`mt-4 mb-4 p-6 border-2 border-dashed rounded-lg transition-all ${
                  'border-blue-400 bg-blue-50'
                }`}
              >
                <p className="text-center text-sm mb-0 text-gray-500">
                  Solte aqui para adicionar um novo grupo
                </p>
              </div>
            )}
            {groups.map((group, index) => (
              <div key={group.id}>
                <div ref={(el) => el && groupRefs.current.set(index, el)}>
                  <GroupItem
                    group={group}
                    index={index}
                    onEdit={onEditGroup}
                    onRemove={onRemoveGroup}
                    onDuplicate={onDuplicateGroup}
                    onAddSection={onAddSection}
                    onEditSection={onEditSection}
                    onRemoveSection={onRemoveSection}
                    onDuplicateSection={onDuplicateSection}
                    onAddQuestion={onAddQuestion}
                    onEditQuestion={onEditQuestion}
                    onRemoveQuestion={onRemoveQuestion}
                    onDuplicateQuestion={onDuplicateQuestion}
                    allGroups={groups}
                    draggedType={draggedType}
                  />
                </div>
                {dropIndex === index + 1 && (
                  <div
                    className={`mt-4 mb-4 p-6 border-2 border-dashed rounded-lg transition-all ${
                      'border-blue-400 bg-blue-50'
                    }`}
                  >
                    <p className="text-center text-sm mb-0 text-gray-500">
                      Solte aqui para adicionar um novo grupo
                    </p>
                  </div>
                )}
              </div>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {groups.length > 0 && (
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onAddGroup()}
          className="text-blue-600 font-medium p-0 mt-4"
        >
          Adicionar Grupo
        </Button>
      )}
    </>
  );
}